<!-- Custom colorpicker based on Iro.js with inputs for RGBA and 6/8-digit hex.

Listen to the 'changed' signal to get the picked color.

-->
<template>
  <v-layout column>
    <v-btn
      v-if="showCloseButton"
      class="ma-0"
      style="position: absolute; right: 4px; top: 4px"
      icon
      small
      :title="$t('close')"
      data-cy="colorpicker-btn-close"
      @click="$emit('close')"
    >
      <v-icon small>
        close
      </v-icon>
    </v-btn>
    <v-layout
      justify-center
      :wrap="isMobile"
    >
      <!-- Iro.js Picker -->
      <div :class="{'mr-4': $vuetify.breakpoint.smAndUp}">
        <div ref="picker" />
      </div>
      <!-- Inputs -->
      <v-layout
        column
        wrap
        class="mb-auto"
        :class="{'mt-2': $vuetify.breakpoint.xs}"
        :style="{
          'min-width': colorpickerWidth + 'px',
          'max-width': colorpickerWidth + 'px',
        }"
      >
        <v-layout wrap>
          <v-layout column>
            <div class="color-label">
              {{ $t('colorPickerLabelPrevious') }}
            </div>
            <div
              class="color-box-container transparency-checker-background small"
            >
              <div
                class="color-box clickable"
                :style="{background: '#' + colorHexPrevious}"
                :title="$t('colorPickerResetToPrevious')"
                @click="resetToPreviousColor()"
              />
            </div>
          </v-layout>
          <v-layout column>
            <div class="color-label">
              {{ $t('colorPickerLabelCurrent') }}
            </div>
            <div
              class="color-box-container transparency-checker-background small"
            >
              <div
                class="color-box no-left-border"
                :style="{background: '#' + colorHex}"
              />
            </div>
          </v-layout>
        </v-layout>
        <!-- RGB -->
        <v-layout
          v-if="!isMobile"
          class="mt-2"
        >
          <v-text-field
            v-model="colorR"
            type="number"
            :label="$t('colorRed')"
            class="mr-2"
            :min="rgbMin"
            :max="rgbMax"
            :rules="[rules.rgbMin, rules.rgbMax]"
          />
          <v-text-field
            v-model="colorG"
            type="number"
            :label="$t('colorGreen')"
            class="mr-2"
            :min="rgbMin"
            :max="rgbMax"
            :rules="[rules.rgbMin, rules.rgbMax]"
          />
          <v-text-field
            v-model="colorB"
            type="number"
            :label="$t('colorBlue')"
            class="mr-2"
            :min="rgbMin"
            :max="rgbMax"
            :rules="[rules.rgbMin, rules.rgbMax]"
          />
        </v-layout>
        <!-- Alpha -->
        <v-layout v-if="!isMobile && !ignoreAlpha">
          <v-text-field
            v-model="alpha"
            type="number"
            :label="$t('colorAlpha')"
            :min="alphaMin"
            :max="alphaMax"
            :step="alphaMax / 100"
          />
        </v-layout>
        <!-- HEX/HEXA -->
        <v-text-field
          v-if="!isMobile"
          v-model="colorHex"
          label="Hex"
          :rules="[rules.hexColor]"
          prefix="#"
          data-cy="colorpicker-input-hex"
        >
          <v-layout slot="append-outer">
            <!-- Pipette -->
            <v-btn
              icon
              small
              class="ml-0 mr-1 my-0 grey--text text--darken-2"
              :style="{
                opacity: browserSupportsEyedropperAPI ? 1 : 0.33,
              }"
              :title="
                browserSupportsEyedropperAPI
                  ? $t('colorPipette')
                  : $t('browserDoesNotSupportColorPipette')
              "
              @click="browserSupportsEyedropperAPI ? startColorPipette() : ''"
            >
              <v-icon style="font-size: 20px">
                colorize
              </v-icon>
            </v-btn>
            <!-- Copy -->
            <v-btn
              :title="$t('copyToClipboard')"
              icon
              small
              class="ma-0 grey--text text--darken-2"
              @click="copyHexToClipboard()"
            >
              <v-icon
                :class="{'success--text': copied}"
                style="font-size: 20px"
              >
                {{ copied ? 'check' : 'content_copy' }}
              </v-icon>
            </v-btn>
          </v-layout>
        </v-text-field>
        <!-- Color Palette -->
        <v-layout
          v-if="showColorPalette"
          class="mt-3"
        >
          <div
            v-for="(paletteColor, paletteColorIndex) in colorPalette"
            :key="paletteColor + paletteColorIndex"
            class="
              bordered--subtle
              fake-horizontal-border-collapse
              grow-1
              clickable
            "
            style="min-height: 22px"
            :style="{background: paletteColor}"
            :title="paletteColor"
            @click="onColorPaletteButtonClicked(paletteColor)"
          />
        </v-layout>
      </v-layout>
    </v-layout>
  </v-layout>
</template>

<script>
import {anyColorToHex, copyToClipboard} from '@/utils';

import {default as defaultColorPalette} from '@/mixins/colorPalette.js';

import iro from '@jaames/iro';
import debounce from 'lodash.debounce';
import throttle from 'lodash.throttle';

const DEBOUNCE_MS = 250;
const THROTTLE_MS = 16.7;

export default {
  props: {
    /**
     * Initial color as hexString with or without leading hash.
     */
    color: {
      type: String,
      default: 'ffffff',
    },
    colorPalette: {
      type: Array[String],
      default: () => defaultColorPalette,
    },
    showColorPalette: {
      type: Boolean,
      default: false,
    },
    showCloseButton: {
      type: Boolean,
      default: true,
    },
    ignoreAlpha: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const rgbMin = 0;
    const rgbMax = 255;

    const alphaMin = 0;
    const alphaMax = 1;

    const $t = this.$t;

    const initialColor = this.color.replace('#', '');

    return {
      copied: false,
      patternHex: new RegExp('^#{0,1}([a-fA-F0-9]{6})$', 'i'),
      patternHexa: new RegExp('^#{0,1}([a-fA-F0-9]{8})$', 'i'),

      colorHexPrevious: initialColor,
      colorHex_: initialColor,

      colorR_: 255,
      colorG_: 255,
      colorB_: 255,
      alpha_: 1,

      rgbMin: rgbMin,
      rgbMax: rgbMax,

      alphaMin: alphaMin,
      alphaMax: alphaMax,

      rules: {
        rgbMin: (v) => v >= rgbMin || $t('tooSmall'),
        rgbMax: (v) => v <= rgbMax || $t('tooBig'),
        hexColor: (v) => this.isValidHexColorString(v) || $t('invalidHexColor'),
      },

      iroInstance: null,
    };
  },
  computed: {
    /**
     * Chrome and Edge only for now, also not on mobile.
     *
     * https://caniuse.com/?search=eyedropper
     */
    browserSupportsEyedropperAPI() {
      return window.EyeDropper !== undefined;
    },
    isMobile() {
      return this.$vuetify.breakpoint.xs;
    },
    colorpickerWidth() {
      let width = 200;

      if (this.isMobile) {
        const minWidth = this.$vuetify.breakpoint.width - 140;
        if (width < minWidth) {
          width = minWidth;
        }
      }
      if (this.$vuetify.breakpoint.width < width) {
        width = this.$vuetify.breakpoint.width - 32;
      }
      return width;
    },
    colorpickerHeight() {
      if (this.isMobile) {
        return this.colorpickerWidth / 2;
      }
      return this.colorpickerWidth;
    },
    colorHex: {
      get() {
        return this.colorHex_;
      },
      set(hexString) {
        if (!this.isValidHexColorString(hexString)) {
          return;
        }
        this.colorHex_ = hexString.replace('#', '');

        const hexAttr = this.getRelevantHexStringAttributeByLength(hexString);
        this.iroInstance.color[hexAttr] = hexString;
      },
    },
    colorR: {
      get() {
        return this.colorR_;
      },
      set(value) {
        this.colorR_ = value;
        const preservedAlpha = this.iroInstance.color.alpha;

        if (
          this.iroInstance.color.red !== value ||
          this.iroInstance.color.alpha !== preservedAlpha
        ) {
          this.iroInstance.color.red = value;
          this.iroInstance.color.alpha = preservedAlpha;
        }
      },
    },
    colorG: {
      get() {
        return this.colorG_;
      },
      set(value) {
        this.colorG_ = value;
        const preservedAlpha = this.iroInstance.color.alpha;

        if (
          this.iroInstance.color.green !== value ||
          this.iroInstance.color.alpha !== preservedAlpha
        ) {
          this.iroInstance.color.green = value;
          this.iroInstance.color.alpha = preservedAlpha;
        }
      },
    },
    colorB: {
      get() {
        return this.colorB_;
      },
      set(value) {
        this.colorB_ = value;
        const preservedAlpha = this.iroInstance.color.alpha;

        if (
          this.iroInstance.color.blue !== value ||
          this.iroInstance.color.alpha !== preservedAlpha
        ) {
          this.iroInstance.color.blue = value;
          this.iroInstance.color.alpha = preservedAlpha;
        }
      },
    },
    alpha: {
      get() {
        return this.alpha_;
      },
      set(value) {
        value = Number(value);

        // First two digits are used for '0.' or '1.'.
        // We round to two digits after comma when needed.
        const numDigits = value.toString().length;
        if (numDigits > 4) {
          value = value.toFixed(2);
        }

        this.alpha_ = value;
        this.iroInstance.color.alpha = value;
      },
    },
  },
  mounted() {
    // Initialize the widget.
    const iroLayout = [
      {
        component: iro.ui.Box,
      },
      {
        component: iro.ui.Slider,
        options: {
          sliderType: 'hue',
        },
      },
      {
        component: iro.ui.Slider,
        options: {
          sliderType: 'saturation',
        },
      },
      {
        component: iro.ui.Slider,
        options: {
          sliderType: 'value',
        },
      },
    ];
    if (!this.ignoreAlpha) {
      iroLayout.push({
        component: iro.ui.Slider,
        options: {
          sliderType: 'alpha',
        },
      });
    }
    this.iroInstance = new iro.ColorPicker(this.$refs.picker, {
      color: this.colorHex,
      width: this.colorpickerWidth,
      boxHeight: this.colorpickerHeight,
      padding: 4,
      margin: 8,
      layout: iroLayout,
    });
    this.iroInstance.on('color:change', this.onIroColorChanged);

    // Sync R/G/B widgets in UI with current color when the widget is
    // display initially, otherwise those values are off. We need to
    // skip emitting the color change to parent widgets here though, as
    // that may 'pick' an initial color which is not desired in all
    // cases, e.g. BrandingColorField where we want to start with null.

    const emit = false;
    this.onIroColorChanged(this.iroInstance.color, emit);
  },
  methods: {
    startColorPipette() {
      const vm = this;
      const eyeDropper = new EyeDropper();
      eyeDropper.open().then((result) => {
        const hexColor = anyColorToHex(result.sRGBHex);
        vm.onColorPaletteButtonClicked(hexColor);
      });
    },
    copyHexToClipboard() {
      const vm = this;

      copyToClipboard(this.colorHex, function () {
        vm.copied = true;
        setTimeout(() => (vm.copied = false), 1500);
      });
    },
    getRelevantHexString(iroColor) {
      if (this.ignoreAlpha) {
        return iroColor.hexString;
      }
      return iroColor.rgba.a < this.rgbMax
        ? iroColor.hex8String
        : iroColor.hexString;
    },
    getRelevantHexStringAttributeByLength(hexString) {
      if (this.ignoreAlpha) {
        return 'hexString';
      }
      hexString = hexString.replace('#', '');
      if (hexString.length <= 6) {
        return 'hexString';
      }
      return 'hex8String';
    },
    onColorPaletteButtonClicked(paletteColor) {
      this.colorHex = paletteColor;
      this.fixColorPaletteSameAsPreviousMayNotUpdate();
    },
    fixColorPaletteSameAsPreviousMayNotUpdate() {
      if (
        this.colorHexPrevious &&
        this.colorHexPrevious.toLowerCase() == this.colorHex_.toLowerCase()
      ) {
        // Iro.js may not detect this as a change, meaning e.g. when
        // #ffffff is the initial color on the color wheel, actually
        // selecting #FFFFFF from the color-palette buttons could have
        // no effect. This ensures the color is updated properly.
        const emit = true;
        this.onIroColorChanged(this.iroInstance.color, emit);
      }
    },
    isValidHexColorString(hexString) {
      if (this.ignoreAlpha) {
        return this.patternHex.test(hexString);
      }
      return (
        this.patternHex.test(hexString) || this.patternHexa.test(hexString)
      );
    },
    resetToPreviousColor() {
      const hexAttr = this.getRelevantHexStringAttributeByLength(
        this.colorHexPrevious
      );
      this.iroInstance.color[hexAttr] = this.colorHexPrevious;
    },
    onIroColorChanged(iroColor, emit = true) {
      this.throttledOnColorChangedUpdateDependents(iroColor);
      if (emit) {
        this.debouncedOnColorChangedEmitToParent();
      }
    },
    /**
     * Throttle UI updates as it can get laggy otherwise.
     *
     * Restricting rendering to max. every 16.7ms equals ~ 60fps.
     */
    throttledOnColorChangedUpdateDependents: throttle(function (iroColor) {
      this.colorHex = this.getRelevantHexString(iroColor);

      // Update RGBA UI in picker only.
      this.colorR = iroColor.rgba.r;
      this.colorG = iroColor.rgba.g;
      this.colorB = iroColor.rgba.b;
      this.alpha = iroColor.rgba.a;
    }, THROTTLE_MS),

    debouncedOnColorChangedEmitToParent: debounce(function () {
      // Send new value to parent component.
      this.$emit('changed', this.getRelevantHexString(this.iroInstance.color));
    }, DEBOUNCE_MS),
  },
};
</script>

<style scoped>
.color-label {
  color: rgba(0, 0, 0, 0.54);
  font-size: 12px;
  margin-bottom: 4px;
}

.color-box-container {
  height: 40px;
}

.color-box {
  height: 40px;
  border: 1px solid rgba(0, 0, 0, 0.16);
}
.color-box.no-left-border {
  border-left: none;
}

.fake-horizontal-border-collapse {
  margin-left: -1px;
}
</style>



// WEBPACK FOOTER //
// src/components/ColorPicker.vue